import { useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ApiError,
  differenceForDisplay,
  isoDaysAgo,
  isoToday,
  percentChangeForDisplay,
  signOf,
  type AllocationDimension,
} from '@greencandle/api-client'
import { api } from '../api'
import { Money, Percent } from '../components/Figure'
import { AllocationBar } from '../charts/AllocationBar'
import { EquityCurve } from '../charts/EquityCurve'
import { OptionLadder } from '../charts/OptionLadder'

const RANGES = [
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '1y' },
]

/**
 * FR-H1.
 *
 * Scoped to one account rather than the whole book, because the snapshot series is stored
 * per account and each account records its own base currency (FR-E2). Summing two of them
 * would mean converting between base currencies in the browser, which is exactly what the
 * backend refuses to do silently - a portfolio-wide curve needs a server-side endpoint
 * that aggregates the series, not arithmetic here.
 */
export function DashboardPage() {
  const [accountId, setAccountId] = useState<string | null>(null)
  const [days, setDays] = useState(90)
  const [dimension, setDimension] = useState<AllocationDimension>('ASSET_CLASS')

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.listAccounts() })

  // Until one is chosen, the first account is the subject. Sorted by name server-side, so
  // this is stable between loads rather than whichever row the database returned first.
  const selected = accountId ?? accounts.data?.[0]?.id ?? null

  const snapshots = useQuery({
    queryKey: ['snapshots', selected, days],
    queryFn: () => api.snapshots(selected!, isoDaysAgo(days), isoToday()),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const holdings = useQuery({
    queryKey: ['holdings', selected],
    queryFn: () => api.holdings(selected!),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const twr = useQuery({
    queryKey: ['returns', selected, days],
    queryFn: () => api.returns(selected!, isoDaysAgo(days), isoToday()),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const allocation = useQuery({
    queryKey: ['allocation', selected, dimension],
    queryFn: () => api.allocation({ dimension, accountId: selected! }),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  if (accounts.isPending) {
    return <p className="muted">Loading…</p>
  }
  if (accounts.isError) {
    return <ErrorNotice error={accounts.error} />
  }
  if (accounts.data.length === 0) {
    return (
      <>
        <h1 className="page-title">Dashboard</h1>
        <div className="notice">No accounts yet, so there is nothing to value.</div>
      </>
    )
  }

  const account = accounts.data.find((a) => a.id === selected) ?? accounts.data[0]!
  const series = snapshots.data ?? []
  const latest = series.at(-1)
  const previous = series.at(-2)

  // The headline is today's valuation from FR-E4, not the last stored snapshot: the
  // snapshot is written by a nightly job and would be up to a day stale.
  const total = allocation.data?.total ?? null
  const currency = allocation.data?.currency ?? account.baseCurrency

  const dayChange = differenceForDisplay(latest?.marketValue, previous?.marketValue)
  const dayChangePercent = percentChangeForDisplay(latest?.marketValue, previous?.marketValue)

  return (
    <>
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">{account.name}</p>

      {/* One row, above everything it scopes. Both controls re-render every figure below. */}
      <div className="filters">
        <select
          aria-label="Account"
          value={account.id}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {accounts.data.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.baseCurrency})
            </option>
          ))}
        </select>

        <div className="segmented" role="group" aria-label="Date range">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              aria-pressed={days === range.days}
              onClick={() => setDays(range.days)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {allocation.isError ? (
        <ErrorNotice error={allocation.error} />
      ) : (
        <>
          {/* Exactly one hero figure on the view: the number the dashboard leads with. */}
          <div className="hero">
            <span className="hero-value">
              <Money value={total} currency={currency} display="figure" />
            </span>
            {dayChange && (
              <span className="hero-delta">
                <Delta value={dayChange} percent={dayChangePercent} currency={currency} />
                <span className="muted" style={{ fontWeight: 400 }}>
                  {' '}
                  since {previous?.date}
                </span>
              </span>
            )}
          </div>
          <p className="page-subtitle">
            Valued {allocation.data?.holdingsValued ?? 0} of{' '}
            {allocation.data?.holdingsConsidered ?? 0} holdings, {currency}
          </p>

          <div className="tiles">
            <Tile label="Unrealised P&L">
              <Money
                value={latest?.unrealisedPnl}
                currency={currency}
                display="figure"
                signed
                reason="No snapshot has been taken yet"
              />
              {latest?.unrealisedPnlPercent && (
                <div style={{ fontSize: 13, marginTop: 2 }}>
                  <Percent value={latest.unrealisedPnlPercent} />
                </div>
              )}
            </Tile>

            <Tile label="Cost basis">
              <Money
                value={latest?.costBasis}
                currency={currency}
                display="figure"
                reason="No snapshot has been taken yet"
              />
            </Tile>

            {/*
              FR-E3. Labelled "time-weighted" rather than "return", because the difference from
              the P&L tile beside it is the whole point: this one is unaffected by money going
              in or out, and the two will disagree whenever the account was funded.
            */}
            <Tile label={`Time-weighted return · ${days}d`}>
              {twr.data?.twrPercent ? (
                <>
                  <Percent value={twr.data.twrPercent} display="figure" />
                  {twr.data.annualisedPercent && (
                    <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                      <Percent value={twr.data.annualisedPercent} /> annualised
                    </div>
                  )}
                </>
              ) : (
                <span className="unknown" title={twr.data?.note ?? 'Not enough history yet'}>
                  not available
                </span>
              )}
            </Tile>

            <Tile label="Snapshots">
              <span className="figure">{series.length}</span>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                {series.length === 0
                  ? 'none in this range'
                  : `to ${latest?.date ?? '—'}`}
              </div>
            </Tile>
          </div>

          <div className="charts">
            <EquityCurve
              snapshots={series}
              currency={currency}
              dimmed={snapshots.isFetching && !snapshots.isPending}
            />
            {allocation.data && (
              <AllocationBar
                report={allocation.data}
                dimension={dimension}
                onDimensionChange={setDimension}
                dimmed={allocation.isFetching && !allocation.isPending}
              />
            )}
          </div>

          {/* Renders nothing when the account holds no options. */}
          {holdings.data && <OptionLadder holdings={holdings.data} currency={currency} />}
        </>
      )}
    </>
  )
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{children}</div>
    </div>
  )
}

/**
 * A signed change. Direction is carried by the arrow and the sign as well as the colour,
 * so it survives colour-vision deficiency, greyscale printing and forced-colors.
 */
function Delta({
  value,
  percent,
  currency,
}: {
  value: string
  percent: string | null
  currency: string
}) {
  // Flat is its own state. Treating zero as a rise painted an unchanged portfolio green with
  // an up arrow, which is the one reading that is definitely wrong.
  const direction = signOf(value) ?? 'flat'
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■'
  const described =
    direction === 'up' ? ' increase' : direction === 'down' ? ' decrease' : ' unchanged'

  return (
    <span className={direction === 'flat' ? 'muted' : direction}>
      <span aria-hidden="true">{arrow} </span>
      <Money value={value} currency={currency} display="figure" signed />
      {percent && (
        <>
          {' ('}
          <Percent value={percent} />
          {')'}
        </>
      )}
      <span className="visually-hidden">{described}</span>
    </span>
  )
}

function ErrorNotice({ error }: { error: unknown }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof ApiError
        ? (error.problem.detail ?? error.message)
        : 'Could not reach the API.'}
    </div>
  )
}
